import { callUiApi } from '../../../common/fetch.js';
import { invalidPhoneNumberMsg, t } from '../../../common/i18n/index.js';
import { PHONE_NUMBER_REGEX } from '../../../common/utils/phoneNumberUtils.js';
import { PrereserveNumbersRequest } from '../../../generated/api/prereserveNumbersRequest.js';
import {
  getValidatePortablePrivateMethod,
  getValidateSimFreePrivateMethod,
  prereserveNumbersPrivateMethod,
} from '../../../generated/api/uiApiMethods.js';
import type { ValidatePortableResponse } from '../../../generated/api/validatePortableResponse.js';
import type { ValidateSimFreeResponse } from '../../../generated/api/validateSimFreeResponse.js';

/**
 * Prereserve phone numbers for new voice subscriptions
 * @param mdmId Company MDM ID
 * @param count Number of phone numbers to prereserve (default 6)
 */
export const fetchPrereserveNumbers = async (mdmId: string, count: number = 6): Promise<string[]> => {
  const response = await callUiApi(prereserveNumbersPrivateMethod(), {
    mdmId,
    body: JSON.stringify({
      count,
      numberPool: PrereserveNumbersRequest.NumberPoolEnum.VOICE,
    }),
  });
  const data = await response.json();
  return data.numbers ?? [];
};

/**
 * Validate a SIM card number
 * @param iccidNumber SIM card ICCID number
 * @param mdmId Company MDM ID
 */
export const fetchValidateSim = async (iccidNumber: string, mdmId: string): Promise<ValidateSimFreeResponse> => {
  const response = await callUiApi(getValidateSimFreePrivateMethod({ iccidNumber }), { mdmId });
  return response.json();
};

/**
 * Validate phone number for portability (MNP)
 * @param phoneNumber Phone number to validate (MSISDN)
 * @param mdmId Company MDM ID
 * @returns ValidatePortableResponse with portable status and any error
 */
export const fetchValidatePhoneNumber = async (
  phoneNumber: string,
  mdmId: string
): Promise<ValidatePortableResponse> => {
  if (!phoneNumber.match(new RegExp(PHONE_NUMBER_REGEX))) {
    return {
      errorMessage: t.YLCX(invalidPhoneNumberMsg),
      errorType: 'INVALID_NUMBER_FORMAT' as ValidatePortableResponse.ErrorTypeEnum,
      portable: false,
    };
  }

  try {
    const response = await callUiApi(getValidatePortablePrivateMethod({ msisdn: phoneNumber }), { mdmId });
    return response.json();
  } catch (error) {
    return {
      errorMessage: error instanceof Error ? error.message : t.T4OU('Technical error, please try again later.'),
      errorType: 'TECHNICAL_ERROR' as ValidatePortableResponse.ErrorTypeEnum,
      portable: false,
    };
  }
};
